import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { estimateAPI } from '../../services/api';

export default function EstimateConfigAdmin() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await estimateAPI.getConfig();
      setConfigs(response.data || []);
    } catch (err) {
      setError('Failed to load estimate configuration: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (config) => {
    setEditing(config.configKey);
    setEditValue(config.configValue.toString());
  };

  const handleCancel = () => {
    setEditing(null);
    setEditValue('');
  };

  const handleSave = async (key) => {
    try {
      setError(null);
      await estimateAPI.updateConfig(key, parseFloat(editValue));
      setSuccess(`Updated "${key}" successfully`);
      setEditing(null);
      setEditValue('');
      loadConfigs();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update: ' + (err.response?.data?.error || err.message));
    }
  };

  const formatConfigValue = (key, value) => {
    if (key === 'price_multiplier') {
      const pct = ((Number(value) - 1) * 100).toFixed(0);
      return `${Number(value).toFixed(4)} (${pct}% markup)`;
    }
    return Number(value).toFixed(4);
  };

  if (loading) {
    return <div className="loading">Loading configuration...</div>;
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>Estimate Configuration</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Link to="/admin/estimate-calculator" className="btn btn-primary">
            Open Calculator
          </Link>
          <Link to="/admin" className="btn btn-secondary">Back to Admin</Link>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="form-section">
        <h2>Price Multiplier</h2>
        <p className="text-muted" style={{ marginBottom: '1rem' }}>
          The locked price is calculated as: <strong>estimate x multiplier</strong>.
          A multiplier of 1.20 means a 20% markup over the cost estimate.
        </p>

        <table className="simple-table">
          <thead>
            <tr>
              <th>Setting</th>
              <th>Value</th>
              <th>Description</th>
              <th>Last Updated</th>
              <th style={{ textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {configs.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center' }} className="text-muted">
                  No configuration found. Run migration 011 to seed defaults.
                </td>
              </tr>
            ) : (
              configs.map(config => {
                const isEditing = editing === config.configKey;
                return (
                  <tr key={config.configKey}>
                    <td>
                      <strong>{config.configKey}</strong>
                    </td>
                    <td>
                      {isEditing ? (
                        <input
                          type="number"
                          className="filter-input"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          step="0.01"
                          min="1"
                          style={{ width: '120px' }}
                          autoFocus
                        />
                      ) : (
                        <span style={{ fontFamily: 'monospace', fontSize: '1rem' }}>
                          {formatConfigValue(config.configKey, config.configValue)}
                        </span>
                      )}
                    </td>
                    <td className="text-muted">{config.description || '-'}</td>
                    <td className="text-muted" style={{ fontSize: '0.8rem' }}>
                      {config.updated_at
                        ? new Date(config.updated_at).toLocaleString()
                        : '-'}
                      {config.updatedBy && ` by ${config.updatedBy}`}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleSave(config.configKey)}
                            disabled={!editValue || parseFloat(editValue) < 1}
                          >
                            Save
                          </button>
                          <button className="btn btn-sm btn-secondary" onClick={handleCancel}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEdit(config)}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Quick reference */}
      <div className="form-section" style={{ marginTop: '1rem' }}>
        <h2>Quick Reference</h2>
        <table className="simple-table">
          <thead>
            <tr>
              <th>Multiplier</th>
              <th>Markup %</th>
              <th>Example ($10,000 estimate)</th>
            </tr>
          </thead>
          <tbody>
            {[1.10, 1.15, 1.20, 1.25, 1.30].map(m => (
              <tr key={m} style={m === 1.20 ? { background: 'var(--bg-muted)', fontWeight: 'bold' } : {}}>
                <td>{m.toFixed(2)}</td>
                <td>{((m - 1) * 100).toFixed(0)}%</td>
                <td>{`$${(10000 * m).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
